.chord {
  position: relative;
  display: inline-flex;
  padding: 1px 6px;
  margin: 2px 2px 4px;
  border-radius: 3px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  vertical-align: baseline;
  line-height: 1.2;
}

.chord--inScale {
  color: var(--tuna-notes-inScale);
  background: color-mix(in srgb, var(--tuna-notes-inScale) 8%, transparent);
}

.chord--outOfScale {
  color: var(--tuna-notes-outOfScale);
  background: color-mix(in srgb, var(--tuna-notes-outOfScale) 8%, transparent);
  /* Non-chromatic indicator (WCAG 2.2 SC 1.4.1 — color must not be the sole means
   * of conveying information). The dotted underline accompanies the color change
   * so color-blind users still get a signal that the chord is outside the key.
   * `text-decoration-color` uses the same out-of-scale token, so the underline
   * picks up theme changes and reads as part of the chord's identity.
   */
  text-decoration: underline dotted;
  text-decoration-color: var(--tuna-notes-outOfScale);
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

/* Match the editor-wide selection identity: same selection token used by native
 * ::selection and the block-level data-in-range bar. One color across text glyphs,
 * per-chord rectangles, and block bars so single-element and range selections read as
 * one consistent system. */
.chord--selected {
  background: var(--tuna-palette-selection);
}

.chord--clickable {
  text-decoration: underline;
}

/* S3a — modal characteristic-note glow (Joni UX spec §2.3).
 *
 * Two simultaneous treatments stacked via two pseudo-elements so neither
 * fights the chip's own `--tuna-notes-inScale` / `--tuna-notes-outOfScale`
 * background tint:
 *   1. Persistent 2px right-edge accent bar (::after) — the non-chromatic
 *      indicator. A vertical bar (not just color) so color-blind users still
 *      get a signal. Uses `--tuna-palette-info-light` and sits inside the
 *      chip's rounded border so it reads as part of the chord identity.
 *   2. Enter-pulse overlay (::before) — one-shot 600ms fading background tint
 *      when the chord first mounts. The pulse is a positioned overlay (not a
 *      background on the chip itself) so the in-scale / out-of-scale tint
 *      persists underneath.
 *
 * `prefers-reduced-motion: reduce` suppresses the enter-pulse animation only;
 * the persistent bar remains so the non-chromatic indicator never disappears.
 */
.chord--modal {
  position: relative;
  overflow: hidden;
}

.chord--modal::before {
  content: '';
  position: absolute;
  inset: 0;
  background: color-mix(in srgb, var(--tuna-palette-info-light) 12%, transparent);
  border-radius: inherit;
  pointer-events: none;
  /* One-shot pulse: fade 12% tint → 0% over 600ms, then hold transparent. */
  animation: modal-pulse 600ms ease-out 1 forwards;
}

.chord--modal::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 2px;
  background: var(--tuna-palette-info-light);
  border-radius: 0 3px 3px 0;
  /* Pointer-events disabled: the bar is a visual indicator only — clicks pass
   * through to the chord chip's onClick. */
  pointer-events: none;
}

@keyframes modal-pulse {
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .chord--modal::before {
    /* Reduced motion: skip the fading overlay entirely. The persistent bar
     * (::after) is the non-chromatic indicator and remains visible. */
    animation: none;
    display: none;
  }
}

/* ── Tension Contour Stripe (Joni visual spec §2–§3, §9) ─────────────────────
 *
 * A bottom-edge under-stripe INSIDE the chip (not the modal ::after, which owns
 * the right edge). Dual-encoded from one analyzer bucket: hue (--tuna-tension-*)
 * here, and HEIGHT (1px→5px) via an inline style on the element so TS owns a
 * single height source (no CSS/TS drift). The chip's `margin-bottom: 4px`
 * already reserves the gutter, so painting the stripe causes zero layout shift.
 *
 * aria-hidden + pointer-events:none — the stripe is decorative-redundant; its
 * meaning is mirrored into the chip's aria-label (visual spec §8).
 */
.tensionStripe {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  /* height set inline (per-bucket, the SC 1.4.1 redundant channel) */
  /* A 3px radius exceeds the rest/low/medium stripe heights (1–3px) and clips
   * their left/right tips into rounded dots. Cap at 1px so the thin buckets
   * read as a clean full-width "ground line" (spec §2 metaphor), while the
   * taller high/peak stripes keep a barely-softened bottom corner. */
  border-radius: 0 0 1px 1px;
  pointer-events: none;
  /* prefers-reduced-motion handled below; default is an instant paint with an
   * opacity fade-in so the stripe doesn't pop as the analyzer resolves. */
  opacity: 1;
  animation: tension-fade-in 240ms ease-out 1;
}

.tensionStripe--rest {
  background: var(--tuna-tension-rest);
}
.tensionStripe--low {
  background: var(--tuna-tension-low);
}
.tensionStripe--medium {
  background: var(--tuna-tension-medium);
}
.tensionStripe--high {
  background: var(--tuna-tension-high);
}
.tensionStripe--peak {
  background: var(--tuna-tension-peak);
}

/* Muted / low-confidence (visual spec §9): TWO muted channels so "uncertain"
 * is itself not colour-only — desaturated (mixed toward disabled-text) AND a
 * dashed fill (a repeating horizontal dash via a gradient overlay). Applied
 * after a bucket class, so it overrides the solid background. */
.tensionStripe--muted {
  /* Desaturate toward the disabled-text token, theme-correct in both modes. */
  background: color-mix(in srgb, var(--tuna-tension-medium) 40%, var(--tuna-palette-text-disabled));
  /* Dash: opaque segment → transparent gap, repeated horizontally. */
  background-image: repeating-linear-gradient(
    to right,
    currentColor 0,
    currentColor 3px,
    transparent 3px,
    transparent 6px
  );
  /* currentColor for the dash inherits the chip text colour; keep it subtle. */
  color: color-mix(in srgb, var(--tuna-palette-text-disabled) 60%, transparent);
}

@keyframes tension-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media (prefers-reduced-motion: reduce) {
  .tensionStripe {
    /* Reduced motion: no fade — instant paint (mirror the modal-glow query). */
    animation: none;
  }
}
